import { useEffect, useRef, useState } from "react";
import { groupedReceiptLines, receiptBrandLabel } from "@/lib/receipt-brands";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "wouter";
import { hasPermission, useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/ui/page-header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { invalidateRecipeCostQueries } from "@/hooks/use-recipes";
import {
  asObjectArray,
  displayReceiptChangeMoney,
  displayReceiptChangeUnitPrice,
  displayReceiptChangeScalar,
  formatReceiptChangeDate,
} from "@/lib/receipt-change-display";
import {
  Loader2, Clock, CheckCheck, XCircle, FileText, User, Calendar,
  ArrowRight, ChevronRight, ChevronDown, ChevronUp, Search, Package, X, ClipboardEdit, MessageSquare, ReceiptText,
} from "lucide-react";
import { invalidateItemPurchasingCosts } from "@/lib/item-purchasing-cost-cache";
import { createReloadMerge, type ReloadMergePlan } from "@/lib/recordReloadMerge";
import { RecordReloadReview } from "@/components/RecordReloadReview";

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  pending:  { label: "Pending Review", color: "bg-amber-100 text-amber-800 border-amber-200" },
  approved: { label: "Approved",       color: "bg-green-100 text-green-800 border-green-200" },
  rejected: { label: "Rejected",       color: "bg-red-100 text-red-700 border-red-200" },
  withdrawn: { label: "Withdrawn", color: "bg-slate-100 text-slate-700 border-slate-200" },
  direct:   { label: "Direct Correction", color: "bg-blue-100 text-blue-800 border-blue-200" },
};

function ValueChange({ label, value, tone }: { label: string; value: string; tone: "before" | "after" }) {
  return (
    <div className={`rounded-lg border px-2.5 py-1.5 ${tone === "before" ? "border-slate-200 bg-slate-50" : "border-red-200 bg-red-50"}`}>
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`mt-0.5 break-words text-sm ${tone === "before" ? "text-slate-600" : "font-semibold text-red-800"}`}>{value}</p>
    </div>
  );
}

function RequestNote({ label, note }: { label: string; note: unknown }) {
  const content = typeof note === "string" ? note.trim() : "";
  if (!content) return null;
  return (
    <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2">
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
        <MessageSquare className="h-3 w-3" /> {label}
      </div>
      <p className="mt-1 whitespace-pre-wrap break-words text-xs leading-relaxed text-amber-950">{content}</p>
    </div>
  );
}

function getChangeCategories(requestedChanges: any): string[] {
  const categories = new Set<string>();
  if (requestedChanges?.type === "cancellation") categories.add("Cancellation");
  if (requestedChanges?.receiptDate !== undefined) categories.add("Receipt date");
  if (requestedChanges?.notes !== undefined) categories.add("Notes");
  if (requestedChanges?.conditionNotes !== undefined) categories.add("Condition");
  if (requestedChanges?.receivedTemperatureC !== undefined) categories.add("Temperature");
  const lines = asObjectArray(requestedChanges?.lines).length
    ? asObjectArray(requestedChanges?.lines)
    : asObjectArray(requestedChanges?.lineChanges);
  if (lines.some((line: any) => line.requestedQty !== undefined || line.receivedQty !== undefined || line.qtyTo !== undefined || line.newQty !== undefined)) {
    categories.add("Quantity");
  }
  if (lines.some((line: any) => line.requestedUnitCost !== undefined || line.unitCost !== undefined || line.costTo !== undefined || line.newCost !== undefined)) {
    categories.add("Unit cost");
  }
  if (!categories.size && (asObjectArray(requestedChanges?.headerChanges).length || lines.length)) categories.add("Receipt details");
  return Array.from(categories);
}

function ChangeTags({ requestedChanges }: { requestedChanges: any }) {
  const categories = getChangeCategories(requestedChanges);
  if (!categories.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {categories.map((category) => (
        <span key={category} className="inline-flex items-center rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-semibold text-red-700">
          {category}
        </span>
      ))}
    </div>
  );
}

export default function ReceiptChanges() {
  const { user, roles, currentWorkspace } = useAuth();
  const workspaceId = currentWorkspace?.id || "";
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [blocker, setBlocker] = useState("");
  const [minAgeDays, setMinAgeDays] = useState("");
  const pageSize = 20;
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewTarget, setReviewTarget] = useState<any>(null);
  const [reviewNote, setReviewNote] = useState("");
  const [reviewConflict, setReviewConflictFlag] = useState(false);
  const [reviewConflictMessage, setReviewConflictMessage] = useState("");
  const setReviewConflict = (value: string | false) => { setReviewConflictFlag(!!value); setReviewConflictMessage(value || ""); };
  const [reviewReloading, setReviewReloading] = useState(false);
  const [reviewReload, setReviewReload] = useState<{ plan: ReloadMergePlan; target: any } | null>(null);
  const reviewSession = useRef(0);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailItem, setDetailItem] = useState<any>(null);
  const [expandedEntries, setExpandedEntries] = useState<Set<string>>(new Set());

  const canReviewChanges = hasPermission(user, "supplier_receipts.review_change_requests", roles);
  const canReviewEntry = (entry: any) => canReviewChanges && entry?.entryType !== "direct_correction"
    && entry?.crStatus === "pending" && !!entry?.crRequestedByUserId && entry.crRequestedByUserId !== user?.id;
  useEffect(() => { setPage(1); }, [search, tab, blocker, minAgeDays, workspaceId]);
  useEffect(() => {
    reviewSession.current++;
    setReviewReload(null); setReviewConflict(false); setReviewReloading(false);
    setDetailOpen(false);
    setReviewOpen(false);
    setDetailItem(null);
    setReviewTarget(null);
  }, [workspaceId, user?.id]);

  const { data: rawChanges, isLoading, isError, error, refetch } = useQuery<{ changes: any[]; total: number }>({
    queryKey: ["/api/supplier-receipts/changes", workspaceId, user?.id, tab, search, blocker, minAgeDays, page],
    queryFn: () => api.supplierReceipts.listChangesPage(workspaceId, {
      status: tab, search, blocker, minAgeDays, page, pageSize,
    }),
    enabled: !!workspaceId,
  });
  const { data: rawPendingChanges } = useQuery<{ total: number }>({
    queryKey: ["/api/supplier-receipts/changes", workspaceId, user?.id, "pending-count"],
    queryFn: () => api.supplierReceipts.listChangesPage(workspaceId, { status: "pending", page: 1, pageSize: 1 }),
    enabled: !!workspaceId,
  });
  const changes = asObjectArray(rawChanges?.changes);
  const total = rawChanges?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  useEffect(() => { if (rawChanges) setPage(current => Math.min(current, totalPages)); }, [rawChanges, totalPages]);
  const { data: receiptPreview, isLoading: receiptPreviewLoading } = useQuery<any>({
    queryKey: ["/api/supplier-receipts", detailItem?.receiptId, workspaceId, "change-review-preview"],
    queryFn: () => api.supplierReceipts.get(detailItem.receiptId, workspaceId),
    enabled: !!detailItem?.receiptId && !!workspaceId && detailOpen,
  });

  const reviewMutation = useMutation({
    mutationFn: ({ id, action, note }: { id: string; action: "approve" | "reject"; note: string }) => {
      const target = reviewTarget?.crId === id ? reviewTarget : detailItem;
      if (reviewConflict || reviewReload || reviewReloading) throw new Error("Reload and review the current receipt before continuing.");
      if (!canReviewEntry(target)) throw new Error("You cannot review this request.");
      return api.supplierReceipts.reviewChangeRequest(id, workspaceId, { action, reviewerNote: note, expectedRevision: target?.recoveryRevision });
    },
    onSuccess: (_, vars) => {
      const reviewedCancellation = (reviewTarget?.crId === vars.id ? reviewTarget : detailItem)?.crRequestedChanges?.type === "cancellation";
      refetch();
      queryClient.invalidateQueries({ queryKey: ["/api/supplier-receipts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/supplier-receipts/changes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/purchase-orders"] });
      if (vars.action === "approve") {
        invalidateRecipeCostQueries(queryClient, workspaceId);
        void invalidateItemPurchasingCosts(queryClient, workspaceId);
      }
      toast({
        title: reviewedCancellation
          ? vars.action === "approve" ? "Cancellation approved" : "Cancellation rejected"
          : vars.action === "approve" ? "Change request approved and applied" : "Change request rejected",
      });
      setReviewOpen(false);
      setReviewTarget(null);
      setReviewNote("");
      setDetailOpen(false);
      setDetailItem(null);
    },
    onError: (e: any) => {
      if (e.status === 409) setReviewConflict(e.message || "Your changes are still here. Press Reload to review the saved receipt before saving again.");
      toast({ title: "Error", description: e.message, variant: "destructive" });
    },
  });
  const receiptReviewSnapshot = (receipt: any, target: any) => ({
    receiptStatus: receipt?.status, requestStatus: target.crStatus,
    requestedChanges: target.crRequestedChanges,
    ...Object.fromEntries(["receiptDate", "notes", "conditionNotes", "receivedTemperatureC", "attachmentUrl", "attachmentName", "paymentMethod", "paymentTerms", "dueDate"].map(key => [key, receipt?.[key] ?? null])),
    ...Object.fromEntries((receipt?.lines || []).map((line: any) => [`line:${line.id}`, {
      item: line.itemName || line.itemId, quantity: Number(line.receivedQty), unitCost: Number(line.unitCost),
    }])),
  });
  const reloadReview = async () => {
    const session = reviewSession.current;
    const target = reviewOpen ? reviewTarget : detailItem;
    if (!target) return;
    setReviewReloading(true);
    try {
      const [receipt, requests] = await Promise.all([
        api.supplierReceipts.get(target.receiptId, workspaceId),
        api.supplierReceipts.listChangeRequests(target.receiptId, workspaceId),
      ]);
      if (session !== reviewSession.current) return;
      const request = requests.find((entry: any) => entry.id === target.crId);
      if (!request) throw new Error("This request is no longer available.");
      const next = { ...target, recoveryRevision: receipt.recoveryRevision, reviewReceipt: receipt,
        crStatus: request.status, crRequestedChanges: request.requestedChanges,
        crRequestedByUserId: request.requestedByUserId };
      const base = receiptReviewSnapshot(target.reviewReceipt, target);
      setReviewReload({ plan: createReloadMerge(base, base, receiptReviewSnapshot(receipt, next)), target: next });
    } catch (error: any) { if (session === reviewSession.current) toast({ title: "Could not load receipt", description: error.message, variant: "destructive" }); }
    finally { if (session === reviewSession.current) setReviewReloading(false); }
  };
  const reloadPanel = <>
    {reviewConflict && <div role="alert"><p>{reviewConflictMessage}</p><Button variant="outline" disabled={reviewReloading || !!reviewReload} onClick={reloadReview}>{reviewReloading ? "Loading…" : "Reload current receipt"}</Button></div>}
    {reviewReload && <><p role="status">Current request status: {reviewReload.target.crStatus}</p><RecordReloadReview plan={reviewReload.plan} labels={{ receiptStatus: "Receipt status", requestStatus: "Request status", requestedChanges: "Requested changes" }} choices={{}} onChoice={() => {}} onApply={() => {
      if (reviewOpen) setReviewTarget(reviewReload.target); else setDetailItem(reviewReload.target);
      setReviewReload(null); setReviewConflict(false);
    }} onCancel={() => setReviewReload(null)} /></>}
  </>;

  const filtered = changes;
  const pendingCount = rawPendingChanges?.total ?? 0;

  const openReview = async (item: any) => {
    const session = ++reviewSession.current;
    if (!canReviewEntry(item)) return;
    let receipt: any;
    try {
      receipt = await api.supplierReceipts.get(item.receiptId, workspaceId);
      if (session !== reviewSession.current) return;
    } catch (error: any) {
      toast({ title: "Could not load receipt", description: error.message, variant: "destructive" });
      return;
    }
    setReviewConflict(false); setReviewReload(null); setReviewReloading(false);
    setReviewTarget({ ...item, recoveryRevision: receipt.recoveryRevision, reviewReceipt: receipt });
    setReviewNote("");
    setReviewOpen(true);
  };

  const openDetail = async (item: any) => {
    const session = ++reviewSession.current;
    let receipt: any;
    try {
      receipt = await api.supplierReceipts.get(item.receiptId, workspaceId);
      if (session !== reviewSession.current) return;
    } catch (error: any) {
      toast({ title: "Could not load receipt", description: error.message, variant: "destructive" });
      return;
    }
    setReviewNote("");
    setReviewConflict(false); setReviewReload(null); setReviewReloading(false);
    setDetailItem({ ...item, recoveryRevision: receipt.recoveryRevision, reviewReceipt: receipt });
    setDetailOpen(true);
  };

  const toggleExpanded = (entryKey: string) => {
    setExpandedEntries((current) => {
      const next = new Set(current);
      if (next.has(entryKey)) next.delete(entryKey);
      else next.add(entryKey);
      return next;
    });
  };

  const renderLineChanges = (requestedChanges: any) => {
    const requestedLines = asObjectArray(requestedChanges?.lines);
    const lines = requestedLines.length ? requestedLines : asObjectArray(requestedChanges?.lineChanges);
    const headerChanges = asObjectArray(requestedChanges?.headerChanges);
    if (!lines.length && !headerChanges.length) return null;
    return (
      <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white text-xs">
        {headerChanges.length > 0 && (
          <div className="space-y-2 border-b bg-slate-50 p-3">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Receipt details changed</p>
            {headerChanges.map((change: any, index: number) => (
              <div key={index} className="grid gap-2 sm:grid-cols-[minmax(130px,0.7fr)_1fr_auto_1fr] sm:items-center">
                <p className="font-medium capitalize text-slate-700">{displayReceiptChangeScalar(change.field).replace(/([A-Z])/g, " $1")}</p>
                <ValueChange label="Before" value={displayReceiptChangeScalar(change.oldValue ?? change.from)} tone="before" />
                <ArrowRight className="hidden h-4 w-4 text-slate-400 sm:block" />
                <ValueChange label="Requested" value={displayReceiptChangeScalar(change.newValue ?? change.to)} tone="after" />
              </div>
            ))}
          </div>
        )}
        {lines.length > 0 && (
          <div className="divide-y divide-slate-100">
            <div className="bg-slate-50 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Line-item changes</div>
            {lines.map((line: any, index: number) => {
              const receiptLine = asObjectArray(receiptPreview?.lines).find((currentLine: any) => currentLine.id === line.lineId);
              const beforeQty = line.currentQty ?? line.qtyFrom ?? line.oldQty ?? receiptLine?.receivedQty;
              const requestedQty = line.requestedQty ?? line.qtyTo ?? line.newQty;
              const beforeCost = line.currentUnitCost ?? line.costFrom ?? line.oldCost ?? receiptLine?.unitCost;
              const requestedCost = line.requestedUnitCost ?? line.costTo ?? line.newCost;
              return (
                <div key={index} className="p-3">
                  <p className="mb-2 font-semibold text-slate-800">{displayReceiptChangeScalar(line.itemName)}</p>
                  {(Object.prototype.hasOwnProperty.call(line, "brandTo") || Object.prototype.hasOwnProperty.call(line, "brandIdTo")) && <p className="mb-2 rounded border border-amber-200 bg-amber-50 p-2">Brand: {displayReceiptChangeScalar(line.brandNameFrom ?? line.brandFrom, "Unknown brand")} → {displayReceiptChangeScalar(line.brandNameTo ?? line.brandTo, "Unknown brand")}</p>}
                  {(Object.prototype.hasOwnProperty.call(line, "requestedBrandId") || Object.prototype.hasOwnProperty.call(line, "brandId")) && <p className="mb-2 rounded border border-amber-200 bg-amber-50 p-2">
                    Brand: {displayReceiptChangeScalar(receiptLine?.brandName, "Unknown brand")} → {displayReceiptChangeScalar((line.requestedBrandId ?? line.brandId) === receiptLine?.brandId ? receiptLine?.brandName : line.requestedBrandId ?? line.brandId, "Unknown brand")} (explicit receiver selection)
                  </p>}
                  <div className="grid gap-2 sm:grid-cols-2">
                    <div className="rounded-lg border border-slate-100 bg-slate-50 p-2">
                      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Quantity</p>
                      <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-2">
                        <div><p className="mb-1 text-[9px] font-semibold uppercase tracking-wide text-slate-400">Before</p><span className="block rounded bg-white px-2 py-1 text-center tabular-nums text-slate-600">{displayReceiptChangeScalar(beforeQty)}</span></div>
                        <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
                        <div><p className="mb-1 text-[9px] font-semibold uppercase tracking-wide text-red-600">Requested</p><span className="block rounded bg-red-50 px-2 py-1 text-center font-semibold tabular-nums text-red-800">{displayReceiptChangeScalar(requestedQty)}</span></div>
                      </div>
                    </div>
                    <div className="rounded-lg border border-slate-100 bg-slate-50 p-2">
                      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Unit cost</p>
                      <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-2">
                        <div><p className="mb-1 text-[9px] font-semibold uppercase tracking-wide text-slate-400">Before</p><span className="block rounded bg-white px-2 py-1 text-center tabular-nums text-slate-600">{displayReceiptChangeUnitPrice(beforeCost)}</span></div>
                        <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
                        <div><p className="mb-1 text-[9px] font-semibold uppercase tracking-wide text-red-600">Requested</p><span className="block rounded bg-red-50 px-2 py-1 text-center font-semibold tabular-nums text-red-800">{displayReceiptChangeUnitPrice(requestedCost)}</span></div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex-1 overflow-auto p-6">
      <PageHeader
        icon={ClipboardEdit}
        title="Receipt Corrections"
        subtitle="Requested and direct post-confirmation corrections, with requester, reviewer, reason, and before/after history."
      />

      <div className="mt-6 space-y-4">
        {/* Search + stats bar */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by receipt #, supplier, or requester…"
              className="pl-9 h-9 text-sm"
            />
          </div>
          <label className="text-xs text-muted-foreground">Blocker
            <select aria-label="Blocker" value={blocker} onChange={event => setBlocker(event.target.value)} className="ml-2 rounded border bg-background p-2 text-foreground">
              <option value="">All</option><option value="cancellation">Cancellation awaiting review</option><option value="pricing">Awaiting pricing</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-xs text-muted-foreground">At least
            <Input aria-label="Minimum age in days" type="number" min="0" max="36500" value={minAgeDays} onChange={event => setMinAgeDays(event.target.value)} className="h-9 w-20" /> days old
          </label>
          {pendingCount > 0 && canReviewChanges && (
            <Badge className="bg-amber-100 text-amber-800 border border-amber-200 gap-1">
              <Clock className="h-3 w-3" /> {pendingCount} pending review
            </Badge>
          )}
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="pending" className="gap-1.5">
              Pending {pendingCount > 0 && <span className="bg-amber-200 text-amber-900 rounded-full px-1.5 py-0.5 text-xs">{pendingCount}</span>}
            </TabsTrigger>
            <TabsTrigger value="approved">Approved</TabsTrigger>
            <TabsTrigger value="rejected">Rejected</TabsTrigger>
            <TabsTrigger value="withdrawn">Withdrawn</TabsTrigger>
          </TabsList>

          <TabsContent value={tab} className="mt-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : isError ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="font-medium text-destructive">Receipt corrections could not be loaded</p>
                  <p className="mt-1 text-sm text-muted-foreground">{error instanceof Error ? error.message : "Please try again."}</p>
                  <Button variant="outline" className="mt-4" onClick={() => refetch()}>Try again</Button>
                </CardContent>
              </Card>
            ) : filtered.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  <FileText className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">No receipt corrections found</p>
                  <p className="text-sm mt-1">Requested and direct corrections to confirmed receipts appear here.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {filtered.map((c: any) => {
                  const entryKey = `${c.entryType || "request"}-${c.crId}`;
                  const cfg = STATUS_CONFIG[c.crStatus] || STATUS_CONFIG.pending;
                  const isCancellationRequest = c.crRequestedChanges?.type === "cancellation";
                  const requestedLines = asObjectArray(c.crRequestedChanges?.lines);
                  const changedLines = requestedLines.length ? requestedLines : asObjectArray(c.crRequestedChanges?.lineChanges);
                  const hasLineChanges = changedLines.length > 0;
                  const isExpanded = expandedEntries.has(entryKey);
                  return (
                    <Card key={entryKey} className={`hover:shadow-sm transition-shadow ${c.crStatus === "pending" ? "border-amber-300 bg-amber-50/40" : c.entryType === "direct_correction" ? "border-blue-300 bg-blue-50/30" : ""}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          {/* Left: receipt info */}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <button
                                type="button"
                                onClick={() => openDetail(c)}
                                className="flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
                                aria-label={`Open ${c.receiptNumber} for review`}
                              >
                                <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                                {c.receiptNumber}
                                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                              </button>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.color}`}>
                                {cfg.label}
                              </span>
                              {isCancellationRequest && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-rose-100 text-rose-700">
                                  {c.crRequestedChanges?.completionMode ? "Draft cancelled" : "Cancellation request"}
                                </span>
                              )}
                              {c.crStatus === "pending" && <span className="text-xs text-muted-foreground">{Math.max(0, Math.floor((Date.now() - new Date(c.crCreatedAt).getTime()) / 86400000))} days awaiting review</span>}
                              {hasLineChanges && (
                                <span className="inline-flex items-center gap-0.5 rounded-full border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] text-slate-600">
                                  <Package className="h-3 w-3" /> {changedLines.length} line{changedLines.length !== 1 ? "s" : ""}
                                </span>
                              )}
                              <ChangeTags requestedChanges={c.crRequestedChanges} />
                            </div>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                              <span className="font-medium text-foreground">{c.supplierName}</span>
                              {c.branchName && <span>· {c.branchName}</span>}
                              {c.receiptDate && <span className="flex items-center gap-0.5"><Calendar className="h-3 w-3" />{formatReceiptChangeDate(c.receiptDate, "d MMM yyyy")}</span>}
                            </div>
                          </div>

                          {/* Right: actions */}
                          <div className="flex items-center gap-2 shrink-0">
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openDetail(c)}>
                              Open receipt
                            </Button>
                            <Link href={`/supplier-receipts?focus=${encodeURIComponent(c.receiptId)}`}><Button size="sm" variant="outline" className="h-7 text-xs">Resume receipt</Button></Link>
                            {canReviewEntry(c) && (
                              <Button size="sm" className="h-7 text-xs gap-1 bg-green-600 hover:bg-green-700" onClick={() => openReview(c)}>
                                <CheckCheck className="h-3.5 w-3.5" /> Review
                              </Button>
                            )}
                            {(hasLineChanges || getChangeCategories(c.crRequestedChanges).length > 0) && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 gap-1 px-2 text-xs text-slate-600"
                                onClick={() => toggleExpanded(entryKey)}
                                aria-expanded={isExpanded}
                              >
                                {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                                {isExpanded ? "Collapse" : "Expand"}
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* Requester row */}
                        <div className="mt-3 flex items-start gap-3 border-t pt-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-0.5">
                              <User className="h-3 w-3" />
                              <span className="font-medium text-foreground">{c.crRequestedByUserName || "Unknown"}</span>
                              <span>{c.entryType === "direct_correction" ? "applied a direct correction" : isCancellationRequest ? "requested cancellation" : "requested"}</span>
                              {c.crCreatedAt && <span>{formatReceiptChangeDate(c.crCreatedAt, "d MMM yyyy, HH:mm")}</span>}
                            </div>
                            <RequestNote label={c.entryType === "direct_correction" ? "Correction reason" : "Requester’s reason"} note={c.crRequesterNote} />
                          </div>
                          {/* Reviewer row */}
                          {c.crStatus === "withdrawn" && <div className="flex-1 min-w-0 border-l pl-3 text-xs text-muted-foreground">
                            Withdrawn by requester {formatReceiptChangeDate(c.crWithdrawnAt, "d MMM yyyy, HH:mm")}
                            <RequestNote label="Withdrawal reason" note={c.crWithdrawalReason} />
                          </div>}
                          {c.entryType !== "direct_correction" && ["approved", "rejected"].includes(c.crStatus) && (
                            <div className="flex-1 min-w-0 border-l pl-3">
                              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-0.5">
                                {c.crStatus === "approved"
                                  ? <CheckCheck className="h-3 w-3 text-green-600" />
                                  : <XCircle className="h-3 w-3 text-red-500" />}
                                <span className="font-medium text-foreground">{c.crReviewedByUserName || "Admin"}</span>
                                <span>{c.crStatus === "approved" ? "approved" : "rejected"}</span>
                                {c.crReviewedAt && <span>{formatReceiptChangeDate(c.crReviewedAt, "d MMM yyyy, HH:mm")}</span>}
                              </div>
                              <RequestNote label="Reviewer’s note" note={c.crReviewerNote} />
                            </div>
                          )}
                        </div>

                        {/* Line changes preview */}
                         {isExpanded && renderLineChanges(c.crRequestedChanges)}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
        {!isError && <div className="flex items-center justify-between gap-3 text-sm">
          <span>{total} entries · Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <Button variant="outline" disabled={page === 1 || isLoading} onClick={() => setPage(current => current - 1)}>Previous</Button>
            <Button variant="outline" disabled={page >= totalPages || isLoading} onClick={() => setPage(current => current + 1)}>Next</Button>
          </div>
        </div>}
      </div>

      {/* ── Detail Dialog ───────────────────────────────────────────────────── */}
      <Dialog open={detailOpen} onOpenChange={o => { if (!o) setDetailItem(null); setDetailOpen(o); }}>
        <DialogContent className="max-h-[90vh] sm:max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ReceiptText className="h-4 w-4" />
              Receipt review — {detailItem?.receiptNumber}
            </DialogTitle>
          </DialogHeader>
          {detailItem && (
            <div className="space-y-4 text-sm">
              <section className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Supplier receipt</p>
                    <p className="mt-1 text-base font-semibold text-slate-900">{receiptPreview?.receiptNumber || detailItem.receiptNumber}</p>
                    <p className="mt-0.5 text-sm text-slate-600">{receiptPreview?.supplierName || detailItem.supplierName}</p>
                  </div>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${(STATUS_CONFIG[detailItem.crStatus] || STATUS_CONFIG.pending).color}`}>
                    {(STATUS_CONFIG[detailItem.crStatus] || STATUS_CONFIG.pending).label}
                  </span>
                </div>
                {receiptPreviewLoading ? (
                  <div className="mt-3 flex items-center gap-2 text-xs text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading receipt details…</div>
                ) : (
                  <div className="mt-4 grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
                    <div><p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Location</p><p className="mt-0.5 font-medium text-slate-800">{receiptPreview?.branchName || detailItem.branchName || "—"}</p></div>
                    <div><p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Receipt date</p><p className="mt-0.5 font-medium text-slate-800">{formatReceiptChangeDate(receiptPreview?.receiptDate || detailItem.receiptDate, "d MMM yyyy")}</p></div>
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Purchase order</p>
                      {receiptPreview?.poId && receiptPreview?.poNumber ? (
                        <Link
                          href={`/purchase-orders?focus=${receiptPreview.poId}`}
                          className="mt-0.5 inline-flex font-medium text-primary underline-offset-4 hover:underline"
                        >
                          {receiptPreview.poNumber}
                        </Link>
                      ) : (
                        <p className="mt-0.5 font-medium text-slate-800">{receiptPreview?.poNumber || "Not linked"}</p>
                      )}
                    </div>
                    <div><p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Receipt total</p><p className="mt-0.5 font-medium text-slate-800">{displayReceiptChangeMoney(receiptPreview?.totalCost ?? detailItem.receiptTotalCost)}</p></div>
                  </div>
                )}
                {!receiptPreviewLoading && asObjectArray(receiptPreview?.lines).length > 0 && (
                  <div className="mt-4 border-t border-slate-200 pt-3">
                    <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                      {detailItem.crStatus === "pending" ? "Receipt values before this request" : "Current receipt items"}
                    </p>
                    <div className="max-h-40 divide-y divide-slate-200 overflow-y-auto rounded-lg border border-slate-200 bg-white">
                      {groupedReceiptLines<any>(asObjectArray(receiptPreview.lines)).map((line: any, index: number) => (
                        <div key={line.id || index} className="grid grid-cols-[1fr_auto_auto] gap-3 px-3 py-2 text-xs">
                          <span className="font-medium text-slate-700">{line.splitGroupId ? "↳ " : ""}{line.itemName || "Item"} {receiptBrandLabel(line)}</span>
                          <span className="tabular-nums text-slate-600">Qty {displayReceiptChangeScalar(line.receivedQty)}</span>
                          <span className="tabular-nums text-slate-600">Cost {displayReceiptChangeUnitPrice(line.unitCost)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </section>

              <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <User className="h-3.5 w-3.5" />
                  <span className="font-semibold text-foreground">{detailItem.crRequestedByUserName || "Unknown"}</span>
                  <span>·</span>
                  <span>{formatReceiptChangeDate(detailItem.crCreatedAt, "d MMM yyyy, HH:mm")}</span>
                </div>
                <RequestNote label={detailItem.entryType === "direct_correction" ? "Correction reason" : "Requester’s reason"} note={detailItem.crRequesterNote} />
              </div>

              {detailItem.crRequestedChanges?.cancellationSnapshot && <section className="rounded-xl border p-3">
                <h3 className="mb-2 text-sm font-semibold">Lines retained at cancellation</h3>
                <table className="w-full text-xs"><thead><tr><th className="text-left">Item</th><th>Quantity</th><th>Unit</th><th>Unit cost</th></tr></thead>
                  <tbody>{groupedReceiptLines<any>(asObjectArray(detailItem.crRequestedChanges.cancellationSnapshot.lines)).map((line: any, index: number) =>
                    <tr key={line.id || index}><td className="py-1">{line.splitGroupId ? "↳ " : ""}{line.itemName || line.itemId} {receiptBrandLabel(line)}</td><td className="text-center">{displayReceiptChangeScalar(line.receivedQty)}</td><td className="text-center">{line.unit}</td><td className="text-center">{displayReceiptChangeUnitPrice(line.unitCost)}</td></tr>)}</tbody>
                </table>
              </section>}
              {(asObjectArray(detailItem.crRequestedChanges?.lines).length > 0
                || asObjectArray(detailItem.crRequestedChanges?.lineChanges).length > 0
                || asObjectArray(detailItem.crRequestedChanges?.headerChanges).length > 0) && (
                <div className="space-y-1.5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-xs font-semibold text-slate-700">
                      {detailItem.entryType === "direct_correction" ? "Applied Changes" : "Proposed Changes"}
                    </p>
                    <ChangeTags requestedChanges={detailItem.crRequestedChanges} />
                  </div>
                  {renderLineChanges(detailItem.crRequestedChanges)}
                </div>
              )}

              {detailItem.crStatus === "withdrawn" && <div className="rounded-xl border p-3 text-xs">
                Withdrawn by requester {formatReceiptChangeDate(detailItem.crWithdrawnAt, "d MMM yyyy, HH:mm")}
                <RequestNote label="Withdrawal reason" note={detailItem.crWithdrawalReason} />
              </div>}
              {detailItem.entryType !== "direct_correction" && ["approved", "rejected"].includes(detailItem.crStatus) && (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    {detailItem.crStatus === "approved"
                      ? <CheckCheck className="h-3.5 w-3.5 text-green-600" />
                      : <XCircle className="h-3.5 w-3.5 text-red-500" />}
                    <span className="font-semibold text-foreground">{detailItem.crReviewedByUserName || "Admin"}</span>
                    <span>{detailItem.crStatus === "approved" ? "approved" : "rejected"}</span>
                    <span>·</span>
                    <span>{formatReceiptChangeDate(detailItem.crReviewedAt, "d MMM yyyy, HH:mm")}</span>
                  </div>
                  <RequestNote label="Reviewer’s note" note={detailItem.crReviewerNote} />
                </div>
              )}

              {canReviewEntry(detailItem) && (
                <div className="border-t pt-3 space-y-2">
                  {reloadPanel}
                  <p className="text-xs font-semibold text-muted-foreground">Review this request</p>
                  <Textarea
                    value={reviewNote}
                    onChange={e => setReviewNote(e.target.value)}
                    placeholder="Optional reviewer note…"
                    className="text-sm min-h-[70px]"
                  />
                  <div className="flex gap-2">
                    <Button size="sm" className="flex-1 bg-green-600 hover:bg-green-700 gap-1" disabled={reviewMutation.isPending || reviewConflict || reviewReloading || !!reviewReload}
                      onClick={() => reviewMutation.mutate({ id: detailItem.crId, action: "approve", note: reviewNote })}>
                      <CheckCheck className="h-3.5 w-3.5" /> Approve & Apply
                    </Button>
                    <Button size="sm" variant="outline" className="flex-1 gap-1 text-destructive border-destructive/30 hover:bg-destructive/5" disabled={reviewMutation.isPending || reviewConflict || reviewReloading || !!reviewReload || !reviewNote.trim()}
                      onClick={() => reviewMutation.mutate({ id: detailItem.crId, action: "reject", note: reviewNote })}>
                      <XCircle className="h-3.5 w-3.5" /> Reject
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDetailOpen(false); setDetailItem(null); }}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Review Dialog (from list view) ─────────────────────────────────── */}
      <Dialog open={reviewOpen} onOpenChange={o => { if (!o) { setReviewTarget(null); setReviewNote(""); } setReviewOpen(o); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Review Change Request</DialogTitle>
          </DialogHeader>
          {reviewTarget && (
            <div className="space-y-3 text-sm">
              {reloadPanel}
              <div className="rounded-lg border p-3 bg-muted/30 space-y-1">
                <p className="font-semibold">{reviewTarget.receiptNumber} · {reviewTarget.supplierName}</p>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <User className="h-3 w-3" />
                  <span>{reviewTarget.crRequestedByUserName}</span>
                  <span>·</span>
                  <span>{formatReceiptChangeDate(reviewTarget.crCreatedAt, "d MMM yyyy")}</span>
                </div>
                {reviewTarget.crRequesterNote && <p className="text-sm italic text-muted-foreground">"{reviewTarget.crRequesterNote}"</p>}
              </div>
              {asObjectArray(reviewTarget.crRequestedChanges?.lines).length > 0 && renderLineChanges(reviewTarget.crRequestedChanges)}
              <div className="space-y-1">
                <Label className="text-sm">Reviewer note <span className="font-normal text-muted-foreground">(optional)</span></Label>
                <Textarea value={reviewNote} onChange={e => setReviewNote(e.target.value)} placeholder="Add a note for the requester…" className="text-sm min-h-[70px]" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewOpen(false)}>Cancel</Button>
            <Button variant="outline" className="gap-1 text-destructive border-destructive/30 hover:bg-destructive/5" disabled={reviewMutation.isPending || reviewConflict || reviewReloading || !!reviewReload || !canReviewEntry(reviewTarget) || !reviewNote.trim()}
              onClick={() => reviewTarget && reviewMutation.mutate({ id: reviewTarget.crId, action: "reject", note: reviewNote })}>
              <XCircle className="h-3.5 w-3.5" /> Reject
            </Button>
            <Button className="gap-1 bg-green-600 hover:bg-green-700" disabled={reviewMutation.isPending || reviewConflict || reviewReloading || !!reviewReload || !canReviewEntry(reviewTarget)}
              onClick={() => reviewTarget && reviewMutation.mutate({ id: reviewTarget.crId, action: "approve", note: reviewNote })}>
              {reviewMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCheck className="h-3.5 w-3.5" />} Approve & Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
